package com.appsyde.shared.presentation.screen.screen

import androidx.compose.runtime.Composable
import com.appsyde.shared.presentation.style.AppStylesLanding
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.*

@Composable
fun TermsPage() {
    Style(AppStylesLanding)
    LegalPageLayout(
        title = "Terms of Service",
        content = {
            P({ classes(AppStylesLanding.legalUpdated) }) { Text("Last updated: March 10, 2026") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Welcome to AppSyde.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("These Terms of Service (\"Terms\") govern your access to and use of the AppSyde platform and services (the \"Service\"). By accessing or using AppSyde, you agree to be bound by these Terms.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("If you do not agree with these Terms, you must not use the Service.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("1. Description of the Service") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde is a software platform designed to assist support teams in managing and responding to customer support emails.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("The Service may include features such as:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("email conversation management") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("AI-assisted draft responses") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("support context extraction") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("team collaboration tools") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("analytics and insights") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde provides ")
                B { Text("assistance tools only") }
                Text(". All responses sent to customers remain under the control and responsibility of the user.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("2. Account Registration") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("To use AppSyde, you must create an account.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("When creating an account you agree to:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("provide accurate information") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("keep your account credentials secure") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("be responsible for all activity under your account") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("You are responsible for maintaining the confidentiality of your login credentials.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde is not responsible for unauthorized access resulting from compromised account credentials.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("3. Workspace and Team Access") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde workspaces may allow multiple team members to access shared support conversations.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("The workspace owner is responsible for:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("managing user access") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("inviting or removing team members") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("ensuring proper use of the Service within the organization") }
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("4. Gmail Integration") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde integrates with Gmail through Google OAuth to retrieve support emails and provide assistance features.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("By connecting your Gmail account, you authorize AppSyde to access email data required to operate the Service.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Users may revoke Gmail access at any time through their Google account settings.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde uses Gmail data only to provide support assistance features and does not sell or use this data for advertising purposes.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("5. AI-Assisted Features") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde may use artificial intelligence to generate suggested draft responses or analyze support conversations.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AI-generated content is provided ")
                B { Text("as a suggestion only") }
                Text(".")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Users remain fully responsible for reviewing, editing, and approving any message before sending it to customers.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde does not guarantee the accuracy, completeness, or suitability of AI-generated suggestions.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("6. Acceptable Use") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("You agree not to use AppSyde to:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("violate any applicable law or regulation") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("send illegal, harmful, or abusive content") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("attempt to gain unauthorized access to the Service") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("interfere with the operation or security of the platform") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("misuse integrations or APIs") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde reserves the right to suspend or terminate accounts that violate these rules.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("7. Payments and Billing") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Some features of AppSyde may require a paid subscription.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Payments are processed through third-party payment providers such as Stripe.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("By subscribing to a paid plan you agree that:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("subscription fees will be charged according to the selected plan") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("payments may renew automatically unless canceled") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("you are responsible for providing valid payment information") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Failure to complete payment may result in suspension of the Service.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("8. Subscription Changes and Cancellation") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("You may cancel your subscription at any time.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Cancellation will take effect at the end of the current billing period unless otherwise specified.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde reserves the right to modify pricing or subscription plans with reasonable notice.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("9. Availability of the Service") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde strives to maintain a reliable and available service.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("However, the Service may occasionally be unavailable due to:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("maintenance") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("infrastructure issues") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("third-party service disruptions") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde does not guarantee uninterrupted or error-free operation.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("10. Data and Content Responsibility") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Users are responsible for the content they process or send through the Service.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("This includes:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("email responses") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("support communications") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("uploaded data") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde does not review all user content and is not responsible for the accuracy or legality of such content.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("11. Intellectual Property") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("All intellectual property rights related to the AppSyde platform, including software, branding, and design, remain the property of AppSyde.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Users are granted a limited, non-exclusive, non-transferable license to use the Service according to these Terms.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("You may not:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("copy") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("modify") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("reverse engineer") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("redistribute") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("any part of the Service without prior authorization.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("12. Limitation of Liability") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("To the maximum extent permitted by law, AppSyde shall not be liable for:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("indirect or consequential damages") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("loss of business or revenue") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("data loss") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("errors resulting from AI-generated suggestions") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("The Service is provided on an \"as is\" and \"as available\" basis.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Your use of the Service is at your own risk.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("13. Termination") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde may suspend or terminate access to the Service if:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("these Terms are violated") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("payment obligations are not fulfilled") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("misuse of the Service is detected") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Users may stop using the Service at any time.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("14. Changes to the Terms") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde may update these Terms from time to time.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("When changes are made, the \"Last updated\" date will be modified accordingly.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Continued use of the Service after changes indicates acceptance of the updated Terms.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("15. Governing Law") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("These Terms are governed by the laws of France.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Any disputes arising from the use of the Service shall be subject to the jurisdiction of the competent courts in France.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("16. Contact") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("For questions regarding these Terms, please contact:")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Email: ")
                A("mailto:appsyde.tb.help@gmail.com", { classes(AppStylesLanding.navLink) }) { Text("appsyde.tb.help@gmail.com") }
            }
        }
    )
}
